import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calculator, Users, DollarSign, ArrowRight, Info, Download, RotateCcw } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import ShareBar from "@/components/ShareBar";

/**
 * /tools — Free interactive calculators.
 *
 * These are "linkable assets" — pages so useful that industry publications
 * and directories tend to link to them. Two tools ship today:
 *   • Program Startup Cost Estimator
 *   • Staffing FTE Calculator
 *
 * Assumptions are transparent (see the "How this is calculated" toggle on
 * each result) so operators can trust the output. Every result ends with
 * a CTA back to /book because the calculators are the top of the funnel.
 */

// ------------------------ Startup Cost model ------------------------
const STARTUP_PROGRAMS = {
  IOP: {
    label: "Intensive Outpatient (IOP)",
    baseFacility: 55000,   // build-out for a small clinical space
    licensingFees: 3500,   // state application + accreditation deposit
    perSlotCapex: 800,     // furniture, testing, tech per active patient slot
    startupMonthsPreRevenue: 4,
    monthlyBurnPerSlot: 620,
    minSlots: 12,
    typicalStaff: 5,
  },
  PHP: {
    label: "Partial Hospitalization (PHP)",
    baseFacility: 85000,
    licensingFees: 5000,
    perSlotCapex: 1150,
    startupMonthsPreRevenue: 5,
    monthlyBurnPerSlot: 940,
    minSlots: 12,
    typicalStaff: 7,
  },
  RTC: {
    label: "Residential (RTC)",
    baseFacility: 180000,
    licensingFees: 8500,
    perSlotCapex: 6000,
    startupMonthsPreRevenue: 8,
    monthlyBurnPerSlot: 2400,
    minSlots: 8,
    typicalStaff: 14,
  },
  DETOX: {
    label: "Withdrawal Management (Detox)",
    baseFacility: 230000,
    licensingFees: 12000,
    perSlotCapex: 8500,
    startupMonthsPreRevenue: 8,
    monthlyBurnPerSlot: 3800,
    minSlots: 6,
    typicalStaff: 16,
  },
};

// State licensure difficulty multiplier (rough — used only for facility/licensing)
const STATE_MULT = {
  AZ: 1.0, CA: 1.35, NY: 1.4, TX: 1.05, FL: 1.1, WA: 1.15, CO: 1.05, MA: 1.2,
  OR: 1.05, NV: 0.95, PA: 1.05, IL: 1.15, GA: 0.95, NC: 0.95, VA: 1.05,
};

function formatUsd(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// ------------------------ Startup Cost UI ------------------------
function StartupCostCalculator() {
  const [programKey, setProgramKey] = useState("IOP");
  const [state, setState] = useState("AZ");
  const [slots, setSlots] = useState(12);
  const [runwayMonths, setRunwayMonths] = useState(6);

  const model = STARTUP_PROGRAMS[programKey];
  const stateMult = STATE_MULT[state] || 1.0;

  const facility = Math.round(model.baseFacility * stateMult);
  const licensing = Math.round(model.licensingFees * stateMult);
  const capex = Math.round(model.perSlotCapex * slots);
  const preRevenueOps = Math.round(model.monthlyBurnPerSlot * slots * model.startupMonthsPreRevenue);
  const workingCapital = Math.round(model.monthlyBurnPerSlot * slots * runwayMonths);
  const total = facility + licensing + capex + preRevenueOps + workingCapital;

  const reset = () => { setProgramKey("IOP"); setState("AZ"); setSlots(12); setRunwayMonths(6); };

  return (
    <div className="border border-[var(--line)] bg-white p-6 md:p-8" data-testid="startup-cost-calc">
      <div className="flex items-center gap-3 mb-6">
        <Calculator size={18} className="text-[var(--gold)]" />
        <h3 className="font-display text-2xl text-[var(--navy)]">Program Startup Cost Estimator</h3>
      </div>
      <p className="text-sm text-[var(--muted)] mb-8 max-w-2xl">
        Rough capital + working-capital needs to open a new licensed behavioral-health program.
        Assumes lease (not purchase) of clinical space and mid-market wages.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">Program type</span>
          <select value={programKey} onChange={(e) => setProgramKey(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
            data-testid="startup-program">
            {Object.entries(STARTUP_PROGRAMS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">State</span>
          <select value={state} onChange={(e) => setState(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
            data-testid="startup-state">
            {Object.keys(STATE_MULT).sort().map((k) => <option key={k}>{k}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">
            Census / Slots ({slots})
          </span>
          <input type="range" min={model.minSlots} max={40} value={slots}
            onChange={(e) => setSlots(parseInt(e.target.value))}
            className="w-full accent-[var(--navy)]" data-testid="startup-slots" />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">
            Cash runway (months): {runwayMonths}
          </span>
          <input type="range" min={3} max={12} value={runwayMonths}
            onChange={(e) => setRunwayMonths(parseInt(e.target.value))}
            className="w-full accent-[var(--navy)]" data-testid="startup-runway" />
        </label>
      </div>

      <div className="bg-[var(--cream)] border-l-4 border-[var(--gold)] p-6 mb-6" data-testid="startup-result">
        <div className="text-[10px] tracking-widest uppercase text-[var(--gold)] font-bold">ESTIMATED TOTAL CAPITAL NEED</div>
        <div className="font-display text-5xl text-[var(--navy)] mt-2">{formatUsd(total)}</div>
        <div className="text-xs text-[var(--muted)] mt-2">
          For a {model.label.toLowerCase()} in {state} with {slots} slots and {runwayMonths} months post-launch runway
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
        <BreakdownCell label="Facility build-out" value={formatUsd(facility)} />
        <BreakdownCell label="Licensing + accreditation" value={formatUsd(licensing)} />
        <BreakdownCell label="Capex (furniture / tech)" value={formatUsd(capex)} />
        <BreakdownCell label="Pre-revenue ops" value={formatUsd(preRevenueOps)} />
        <BreakdownCell label="Working capital" value={formatUsd(workingCapital)} highlight />
      </div>

      <details className="mt-6 text-xs text-[var(--muted)]">
        <summary className="cursor-pointer text-[var(--navy)] font-bold inline-flex items-center gap-1.5">
          <Info size={12} /> How this is calculated
        </summary>
        <p className="mt-3 leading-relaxed">
          Startup capital = facility build-out + licensing/accreditation + per-slot capex
          + {model.startupMonthsPreRevenue} months of pre-revenue operating costs
          + {runwayMonths} months of working-capital reserve. State multiplier adjusts facility
          and licensing by regulator burden (CA/NY highest; NV lowest of the tracked states).
          Numbers reflect 2025-26 mid-market rates and are illustrative — actual capital
          needs vary ±30% based on payer mix, real-estate market, and staffing model.
        </p>
      </details>

      <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-[var(--line)]">
        <Link to="/book" className="btn btn-navy" data-testid="startup-cta">
          Get a real proforma from Catalyst <ArrowRight size={14} className="ml-2" />
        </Link>
        <button onClick={reset} className="btn btn-ghost inline-flex items-center gap-1.5" data-testid="startup-reset">
          <RotateCcw size={12} /> Reset
        </button>
      </div>
    </div>
  );
}

function BreakdownCell({ label, value, highlight }) {
  return (
    <div className={`p-3 ${highlight ? "bg-[var(--navy)] text-white" : "bg-white border border-[var(--line)] text-[var(--ink)]"}`}>
      <div className={`text-[10px] tracking-widest uppercase ${highlight ? "text-[var(--gold)]" : "text-[var(--muted)]"} font-bold leading-tight`}>{label}</div>
      <div className="font-display text-lg mt-1">{value}</div>
    </div>
  );
}

// ------------------------ Staffing Calculator ------------------------
const STAFFING_MODEL = {
  IOP: {
    label: "Intensive Outpatient",
    // FTE per 10 patients
    clinician: 1.5, // primary counselors
    prescriber: 0.15, // psychiatry / NP
    tech: 0.5,
    supervisor: 0.25,
    admin: 0.5,
    caseMgr: 0.4,
  },
  PHP: {
    label: "Partial Hospitalization",
    clinician: 2.5,
    prescriber: 0.3,
    tech: 1.0,
    supervisor: 0.4,
    admin: 0.6,
    caseMgr: 0.5,
  },
  RTC: {
    label: "Residential",
    // FTE per 10 residents
    clinician: 3.0,
    prescriber: 0.35,
    tech: 6.5, // 24/7 coverage requires ~6-7 FTE
    supervisor: 0.6,
    admin: 0.8,
    caseMgr: 0.6,
  },
  DETOX: {
    label: "Withdrawal Management",
    clinician: 2.0,
    prescriber: 0.6,
    tech: 8.0, // nursing + BHT 24/7
    supervisor: 0.7,
    admin: 0.7,
    caseMgr: 0.5,
  },
};

function StaffingCalculator() {
  const [programKey, setProgramKey] = useState("IOP");
  const [census, setCensus] = useState(20);

  const model = STAFFING_MODEL[programKey];
  const per10 = census / 10;

  const rows = useMemo(() => ([
    { label: "Clinicians (LCSW / LPC / LMFT)", fte: model.clinician * per10 },
    { label: "Prescriber (MD / DO / PMHNP)", fte: model.prescriber * per10 },
    { label: "Tech / BHT / RN coverage", fte: model.tech * per10 },
    { label: "Clinical Supervisor", fte: model.supervisor * per10 },
    { label: "Admin / Intake / Billing", fte: model.admin * per10 },
    { label: "Case Manager / UR", fte: model.caseMgr * per10 },
  ]), [model, per10]);

  const totalFte = rows.reduce((s, r) => s + r.fte, 0);

  return (
    <div className="border border-[var(--line)] bg-white p-6 md:p-8" data-testid="staffing-calc">
      <div className="flex items-center gap-3 mb-6">
        <Users size={18} className="text-[var(--gold)]" />
        <h3 className="font-display text-2xl text-[var(--navy)]">Staffing FTE Calculator</h3>
      </div>
      <p className="text-sm text-[var(--muted)] mb-8 max-w-2xl">
        Minimum clinical + support staffing to safely run your program. Based on national medians;
        payer contracts, state ratios, or accreditor requirements may push these higher.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">Program type</span>
          <select value={programKey} onChange={(e) => setProgramKey(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--line)] bg-[#fafafa] text-sm"
            data-testid="staffing-program">
            {Object.entries(STAFFING_MODEL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-widest uppercase text-[var(--muted)] font-bold mb-1">
            Daily census: {census}
          </span>
          <input type="range" min={4} max={60} value={census}
            onChange={(e) => setCensus(parseInt(e.target.value))}
            className="w-full accent-[var(--navy)]" data-testid="staffing-census" />
        </label>
      </div>

      <div className="bg-[var(--cream)] border-l-4 border-[var(--gold)] p-6 mb-6" data-testid="staffing-result">
        <div className="text-[10px] tracking-widest uppercase text-[var(--gold)] font-bold">TOTAL FTE REQUIRED</div>
        <div className="font-display text-5xl text-[var(--navy)] mt-2">{totalFte.toFixed(1)}</div>
        <div className="text-xs text-[var(--muted)] mt-2">for {census} daily census · {model.label}</div>
      </div>

      <div className="border border-[var(--line)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="staffing-breakdown">
          <thead className="bg-[var(--cream)] text-[10px] uppercase tracking-widest text-[var(--muted)]">
            <tr>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-right px-4 py-3">FTE</th>
              <th className="text-right px-4 py-3">Bodies (assume 1.15 coverage)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-[var(--line)]">
                <td className="px-4 py-3 text-[var(--ink)]">{r.label}</td>
                <td className="px-4 py-3 text-right font-bold text-[var(--navy)]">{r.fte.toFixed(1)}</td>
                <td className="px-4 py-3 text-right text-[var(--muted)]">{Math.ceil(r.fte * 1.15)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="mt-6 text-xs text-[var(--muted)]">
        <summary className="cursor-pointer text-[var(--navy)] font-bold inline-flex items-center gap-1.5">
          <Info size={12} /> How this is calculated
        </summary>
        <p className="mt-3 leading-relaxed">
          FTE ratios based on 2024-2026 industry medians across accredited programs, adjusted for
          direct-care coverage requirements (24/7 for RTC/Detox). "Bodies" applies a 1.15 coverage
          multiplier to absorb PTO, sick time, and open shifts. This is a floor, not a ceiling —
          your state licensure ratios or Joint Commission / CARF standards may require more.
        </p>
      </details>

      <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-[var(--line)]">
        <Link to="/hr" className="btn btn-navy" data-testid="staffing-cta">
          Get a custom staffing plan <ArrowRight size={14} className="ml-2" />
        </Link>
      </div>
    </div>
  );
}

// ------------------------ Page ------------------------
export default function Tools() {
  return (
    <SiteLayout branch="main">
      <SEO
        title="Free Behavioral Health Tools — Startup Cost & Staffing Calculators"
        description="Free calculators for behavioral-health operators: estimate program startup capital and required clinical FTE. Trusted by operators nationwide."
        path="/tools"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Catalyst BH Free Tools",
          url: "https://catalystbhconsulting.com/tools",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      <section className="section bg-[var(--navy)] text-white">
        <div className="max-w-[1200px] mx-auto">
          <span className="eyebrow-light">FREE TOOLS · NO SIGNUP REQUIRED</span>
          <h1 className="font-display mt-6 text-[clamp(40px,5vw,64px)] leading-[1.02] max-w-3xl">
            Calculators built by <span className="text-[var(--gold)]">behavioral-health operators</span> for behavioral-health operators.
          </h1>
          <p className="mt-6 text-white/80 max-w-xl text-lg">
            Two free tools we use with every consulting engagement — now yours to use, share, and cite.
            No email required.
          </p>
        </div>
      </section>

      <section className="section bg-white space-y-16" aria-labelledby="tools-h">
        <div className="max-w-[1000px] mx-auto space-y-16">
          <h2 id="tools-h" className="sr-only">Free calculators</h2>
          <StartupCostCalculator />
          <StaffingCalculator />

          <div className="pt-8 border-t border-[var(--line)]">
            <ShareBar
              title="Free Behavioral Health Tools — Startup Cost & Staffing Calculators"
              description="Two free calculators for BH operators — no signup."
              url="https://catalystbhconsulting.com/tools"
            />
          </div>
        </div>
      </section>

      <section className="section bg-[var(--cream)] text-center">
        <div className="max-w-[900px] mx-auto">
          <span className="eyebrow-dark">HELP US IMPROVE THE TOOLS</span>
          <h2 className="font-display mt-5 text-[clamp(28px,3vw,40px)] leading-[1.1] text-[var(--navy)]">
            Have a calculation you wish existed? Tell us — we'll build it.
          </h2>
          <Link to="/book" className="btn btn-navy mt-8 inline-flex">Suggest a Tool <ArrowRight size={14} className="ml-2" /></Link>
        </div>
      </section>
    </SiteLayout>
  );
}
